import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/http';
import { Notice, errorMessage } from '../../components/common';
import { t } from '../../i18n';
import { formatMinor } from '../../lib/money';

interface TransportView {
  currency: string;
  seat: {
    routeName: string;
    stopName: string;
    pickupTime: string | null;
    dropTime: string | null;
    monthlyFeeMinor: number;
    vehicleRegistration: string | null;
    driverName: string | null;
    driverPhone: string | null;
  } | null;
  charges: { period: string; amountMinor: number; invoiceNumber: string; invoiceStatus: string }[];
}

/** A parent's view of their child's bus: route, stop, times, driver, and what has been billed. */
export function ChildTransport({ studentId }: { studentId: string }) {
  const view = useQuery({
    queryKey: ['me', 'children', studentId, 'transport'],
    queryFn: () => api<TransportView>(`/me/children/${studentId}/transport`),
  });
  if (view.isPending) return <p role="status">{t('common.loading')}</p>;
  if (view.isError) return <Notice kind="error">{errorMessage(view.error)}</Notice>;
  const { seat, charges, currency } = view.data;
  return (
    <div className="space-y-4">
      {!seat && <p className="text-slate-600">{t('tr.noSeat')}</p>}
      {seat && (
        <section
          aria-label={t('tr.mySeat')}
          className="space-y-1 rounded-lg border border-slate-200 bg-white p-4 text-sm"
        >
          <h2 className="text-lg font-semibold">{seat.routeName}</h2>
          <p>
            {t('tr.stop')}: {seat.stopName}
            {seat.pickupTime ? ` · ${t('tr.pickupAt', { time: seat.pickupTime })}` : ''}
            {seat.dropTime ? ` · ${t('tr.dropAt', { time: seat.dropTime })}` : ''}
          </p>
          <p>
            {t('tr.vehicle')}: {seat.vehicleRegistration ?? '-'} · {t('tr.driver')}:{' '}
            {seat.driverName ?? '-'}
            {seat.driverPhone ? ` (${seat.driverPhone})` : ''}
          </p>
          <p>{t('tr.perMonth', { amount: formatMinor(seat.monthlyFeeMinor, currency) })}</p>
        </section>
      )}
      <section aria-label={t('tr.billedMonths')} className="space-y-2">
        <h2 className="text-lg font-semibold">{t('tr.billedMonths')}</h2>
        {charges.length === 0 && <p className="text-slate-600">{t('tr.noCharges')}</p>}
        <ul className="space-y-2 text-sm">
          {charges.map((c) => (
            <li key={c.period} className="rounded-lg border border-slate-200 bg-white p-3">
              {c.period} · <strong>{formatMinor(c.amountMinor, currency)}</strong> ·{' '}
              {c.invoiceNumber}
              {c.invoiceStatus === 'cancelled' ? ` (${t('tr.cancelled')})` : ''}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
